import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { useParams } from 'next/navigation';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import type { TabChangedPage } from '../events';
import { trackTabChanged } from '../trackers/tab';

// A type alias, not an interface: `useParams` constrains its generic to Next's
// `Params`, and only aliases get the implicit index signature that satisfies it.
type TabRouteParams = {
  chainIdentifier?: string;
  marketId?: string;
  vaultAddress?: string;
};

/**
 * Market and vault detail routes are chain-scoped, and the same vault address
 * (CREATE2) or market id can exist on several networks — so `entity_id` alone
 * would merge per-network tab activity.
 *
 * Guarded rather than calling `getIdByIdentifier` directly: that throws
 * `UnknownChainError` on an unrecognised segment, and this runs inside a click
 * handler where a throw would break the tab.
 */
function resolveChainId(chainIdentifier: string | undefined) {
  if (chainIdentifier === undefined) return undefined;
  if (!InterfaceChainUtils.isDeployedOnChain.identifier(chainIdentifier)) return undefined;
  return InterfaceChainUtils.getIdByIdentifier(chainIdentifier);
}

/**
 * Builds the `onTabChange` handler for a market or vault page tab bar.
 *
 * The entity id is read from the route rather than passed in: `loading.tsx`
 * renders these tab bars before any page data exists, so it cannot be drilled
 * down from the server page. Keeping the read here — rather than inside the
 * shared `ScrollSectionTabs` — is what keeps that component free of any
 * knowledge of market/vault routes.
 */
export function useTrackTabChanged(page) {
  const $ = _c(9);
  const routeParams = useParams();
  let t0;
  if ($[0] !== routeParams.marketId || $[1] !== routeParams.vaultAddress) {
    t0 = (routeParams.marketId ?? routeParams.vaultAddress)?.toLowerCase();
    $[0] = routeParams.marketId;
    $[1] = routeParams.vaultAddress;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const entityId = t0;
  let t1;
  if ($[3] !== routeParams.chainIdentifier) {
    t1 = resolveChainId(routeParams.chainIdentifier);
    $[3] = routeParams.chainIdentifier;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const chainId = t1;
  let t2;
  if ($[5] !== chainId || $[6] !== entityId || $[7] !== page) {
    t2 = tab => trackTabChanged({
      page,
      tab,
      entityId,
      chainId
    });
    $[5] = chainId;
    $[6] = entityId;
    $[7] = page;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}